import dayjs from "dayjs"
import { DateUtils } from "../common/DateUtil"
import { JobStatus } from "./job"

export interface JobLateInput {
  status: JobStatus
  handInDate: Date
  finishedAt: Date | null
  proposalSentAt: Date | null
  lastAddonOrderSentAt: Date | null
  hasAwaitingAddonOrderResponse: boolean
  collectedAt: Date | null
  proposalAcceptedAt: Date | null
}

export interface JobLateResult {
  isLate: boolean
  lateTimeDisplay: string | null
}

function formatHoursDisplay(referenceDate: Date): string {
  const hours = dayjs().diff(dayjs(referenceDate), "hour")
  if (hours >= 24) {
    return `${Math.floor(hours / 24)}d`
  }
  return `${hours}h`
}

function formatDaysDisplay(referenceDate: Date): string {
  const now = dayjs().tz("Europe/Stockholm").startOf("day")
  const ref = dayjs(referenceDate).tz("Europe/Stockholm").startOf("day")
  const days = now.diff(ref, "day")
  return `${days}d`
}

export function getJobLateStatus(input: JobLateInput): JobLateResult {
  const {
    status,
    handInDate,
    finishedAt,
    proposalSentAt,
    lastAddonOrderSentAt,
    hasAwaitingAddonOrderResponse,
    collectedAt,
    proposalAcceptedAt,
  } = input

  // Addon order: late if sent > 1 hour ago.
  // Checked first because an awaiting addon order overrides the displayed
  // status to SERVICE_PROPOSAL_SENT, so the late time must match.
  if (hasAwaitingAddonOrderResponse && lastAddonOrderSentAt !== null) {
    if (dayjs().diff(dayjs(lastAddonOrderSentAt), "hour") >= 1) {
      return {
        isLate: true,
        lateTimeDisplay: formatHoursDisplay(lastAddonOrderSentAt),
      }
    }
    return { isLate: false, lateTimeDisplay: null }
  }

  // BOOKED / CHECKED_IN: late if handInDate is before today (Stockholm tz)
  if (status === JobStatus.BOOKED || status === JobStatus.CHECKED_IN) {
    if (DateUtils.isPastInStockholm(handInDate)) {
      return {
        isLate: true,
        lateTimeDisplay: formatDaysDisplay(handInDate),
      }
    }
    return { isLate: false, lateTimeDisplay: null }
  }

  // COLLECTED / INSPECTION_DRAFT_SAVED: late if in status > 1 day
  if (
    status === JobStatus.COLLECTED ||
    status === JobStatus.INSPECTION_DRAFT_SAVED
  ) {
    if (collectedAt !== null && dayjs().diff(dayjs(collectedAt), "day") >= 1) {
      return {
        isLate: true,
        lateTimeDisplay: formatDaysDisplay(collectedAt),
      }
    }
    return { isLate: false, lateTimeDisplay: null }
  }

  // SERVICE_PROPOSAL_SENT: late if sent > 1 hour ago
  if (status === JobStatus.SERVICE_PROPOSAL_SENT && proposalSentAt !== null) {
    if (dayjs().diff(dayjs(proposalSentAt), "hour") >= 1) {
      return {
        isLate: true,
        lateTimeDisplay: formatHoursDisplay(proposalSentAt),
      }
    }
  }

  // SERVICE_PROPOSAL_ACCEPTED: late if in status > 1 day
  if (status === JobStatus.SERVICE_PROPOSAL_ACCEPTED) {
    if (
      proposalAcceptedAt !== null &&
      dayjs().diff(dayjs(proposalAcceptedAt), "day") >= 1
    ) {
      return {
        isLate: true,
        lateTimeDisplay: formatDaysDisplay(proposalAcceptedAt),
      }
    }
    return { isLate: false, lateTimeDisplay: null }
  }

  // COMPLETED: late if finished > 1 day ago
  if (status === JobStatus.COMPLETED && finishedAt !== null) {
    if (dayjs().diff(dayjs(finishedAt), "day") >= 1) {
      return {
        isLate: true,
        lateTimeDisplay: formatDaysDisplay(finishedAt),
      }
    }
  }

  return { isLate: false, lateTimeDisplay: null }
}
